import { FC } from 'react';
import { Grid } from '@mui/material';

import { IMaterial } from 'src/types';
import { IReferral, ITestPrep, IStaff, IExamSecurity, IAIA } from 'src/types/org/aia';
import { IAddress } from 'src/types/address';

import { useAuth } from 'src/contexts';
import { uploadFile, convertToFormattedDate, convertToIsoDate } from 'src/utils';

import { AddressFields } from 'src/generic/component/address';
import { FileDetails, FileUploader } from 'src/generic/component/file';
import { FieldRow, FieldLabel, Panel, Title, RadioLabel } from 'src/generic/component';
import { IntegerInput, BooleanRadio, Radio, Input, TextArea, Checkboxes, Checkbox } from 'src/generic/component/form';
import { IFieldProps } from 'src/generic/type/form';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { Draft } from 'src/types/common';

const KEY_OTHER = 'Other';
const TYPE_OPTIONS = ['Non-Profit', 'After-School Program', 'Community Engagement Program', 'Community Center', KEY_OTHER];

const ExamSecurityFields: FC<IFieldProps<IExamSecurity>> = ({ item: initial, errors, readOnly, onUpdate }) => {
  const { getError, hasError } = ErrorManager(errors);
  const {
    heldAtVisitingAddress,
    heldAtShippingAddress,
    materialReceivedBy,
    materialReceivedAt,
    materialReceivedAtOther,
    supervisor,
    materialStorageLocation,
    isMaterialStorageLocked,
    allowNonEmployedAccess,
    hasDesignatedTestingRooms,
    hasTestingRoomPowerWifi,
  } = initial;

  const changed = (updates: Draft<IExamSecurity>) => {
    if (onUpdate) {
      onUpdate({ ...initial, ...updates });
    }
  };

  return <>
    <Grid container spacing={5}>
      <Grid item xs={6}>
        <FieldRow>
          <RadioLabel variant="body1">Are tests taken at the visiting address?</RadioLabel>
          <BooleanRadio readOnly={readOnly} error={getError('heldAtVisitingAddress')} value={heldAtVisitingAddress} name='heldAtVisitingAddress'
            onChange={(value) => {
              changed({
                heldAtVisitingAddress: value,
                heldAtShippingAddress: value ? heldAtShippingAddress : undefined
              });
            }} />
        </FieldRow>
        {(heldAtVisitingAddress === false) &&
          <FieldRow>
            <RadioLabel variant="body1">Are tests taken at the shipping address?</RadioLabel>
            <BooleanRadio readOnly={readOnly} error={getError('heldAtShippingAddress')} value={heldAtShippingAddress} name='heldAtShippingAddress'
              onChange={(value) => {
                changed({ heldAtShippingAddress: value });
              }} />
          </FieldRow>
        }
        <FieldRow>
          <FieldLabel variant="body1">Who are the exam testing materials received by?</FieldLabel>
          <Input disabled={readOnly} validation={hasError('materialReceivedBy') ? 'error' : undefined} validationMsg={getError('materialReceivedBy')}
            onChange={(e) => {
              changed({ materialReceivedBy: e.target.value });
            }}
            value={materialReceivedBy}
          />
        </FieldRow>
        <FieldRow>
          <>
            <RadioLabel variant="body1">Where is the exam material received at?</RadioLabel>
            <Radio name='materialReceivedAt' readOnly={readOnly} value={materialReceivedAt} error={getError('materialReceivedAt')}
              options={[{ key: 'Main Office', label: 'Main Office' }, { key: 'Loading Dock', label: 'Loading Dock' }, { key: KEY_OTHER, label: KEY_OTHER }]}
              onChange={(val: any) => { changed({ materialReceivedAt: val }); }}
            />
          </>
        </FieldRow>
        {materialReceivedAt?.includes(KEY_OTHER) &&
          <FieldRow>
            <FieldLabel variant="body1">Specify where exam materials are intended to be received?</FieldLabel>
            <Input disabled={readOnly} validation={hasError('materialReceivedAtOther') ? 'error' : undefined}
              validationMsg={getError('materialReceivedAtOther')}
              onChange={(e) => { changed({ materialReceivedAtOther: e.target.value }); }}
              value={materialReceivedAtOther}
            />
          </FieldRow>
        }
      </Grid>
      <Grid item xs={6}>
        <FieldRow>
          <FieldLabel variant="body1">Who is responsible for overseeing exam security?</FieldLabel>
          <Input disabled={readOnly} validation={hasError('supervisor') ? 'error' : undefined} validationMsg={getError('supervisor')}
            onChange={(e) => {
              changed({ supervisor: e.target.value });
            }}
            value={supervisor}
          />
        </FieldRow>
        <FieldRow>
          <FieldLabel variant="body1">Where are the exam materials going to be stored?</FieldLabel>
          <Input disabled={readOnly} validation={hasError('materialStorageLocation') ? 'error' : undefined} validationMsg={getError('materialStorageLocation')}
            onChange={(e) => {
              changed({ materialStorageLocation: e.target.value });
            }}
            value={materialStorageLocation}
          />
        </FieldRow>
        <FieldRow>
          <RadioLabel variant="body1">Is the exam storage location locked?</RadioLabel>
          <BooleanRadio readOnly={readOnly} error={getError('isMaterialStorageLocked')} value={isMaterialStorageLocked} name='isMaterialStorageLocked'
            onChange={(value) => { changed({ isMaterialStorageLocked: value }); }} />
        </FieldRow>
        <FieldRow>
          <RadioLabel variant="body1">Do any non-employees have access to exam materials?</RadioLabel>
          <BooleanRadio readOnly={readOnly} error={getError('allowNonEmployedAccess')} value={allowNonEmployedAccess} name='allowNonEmployedAccess'
            onChange={(value) => { changed({ allowNonEmployedAccess: value }); }} />
        </FieldRow>
        <FieldRow>
          <RadioLabel variant="body1">Does your facility have designated testing rooms?</RadioLabel>
          <BooleanRadio readOnly={readOnly} error={getError('hasDesignatedTestingRooms')} value={hasDesignatedTestingRooms} name='hasDesignatedTestingRooms'
            onChange={(value) => {
              changed({
                hasDesignatedTestingRooms: value,
                hasTestingRoomPowerWifi: value ? hasTestingRoomPowerWifi : undefined
              });
            }} />
        </FieldRow>
        {hasDesignatedTestingRooms &&
          <FieldRow>
            <RadioLabel variant="body1">Do these testing rooms have power and wifi?</RadioLabel>
            <BooleanRadio readOnly={readOnly} error={getError('hasTestingRoomPowerWifi')} value={hasTestingRoomPowerWifi} name='hasTestingRoomPowerWifi'
              onChange={(value) => { changed({ hasTestingRoomPowerWifi: value }); }} />
          </FieldRow>
        }
      </Grid>
    </Grid>
  </ >;
};

const Referral: FC<IFieldProps<IReferral>> = ({ item: initial, errors, readOnly, onUpdate }) => {
  const {
    name,
    email
  } = initial;

  const { getError, hasError } = ErrorManager(errors);

  const changed = (updates: Draft<IReferral>) => {
    if (onUpdate) {
      onUpdate({ ...initial, ...updates });
    }
  };

  return <>
    <FieldRow>
      <FieldLabel variant="body1">Referral Name</FieldLabel>
      <Input disabled={readOnly} validation={hasError('name') ? 'error' : undefined} validationMsg={getError('name')}
        onChange={(e) => { changed({ name: e.target.value }); }}
        value={name}
      />
    </FieldRow>
    <FieldRow>
      <FieldLabel variant="body1">Referral Email</FieldLabel>
      <Input disabled={readOnly} validation={hasError('email') ? 'error' : undefined} validationMsg={getError('email')}
        onChange={(e) => { changed({ email: e.target.value }); }}
        value={email}
      />
    </FieldRow>
  </>;
};

const TestPrep: FC<IFieldProps<ITestPrep>> = ({ item: initial, errors, readOnly, onUpdate }) => {
  const {
    description,
    allowOutsideStudents,
    hasAdditionalCompensation
  } = initial;

  const changed = (updates: Draft<ITestPrep>) => {
    if (onUpdate) {
      onUpdate({ ...initial, ...updates });
    }
  };

  const { getError, hasError } = ErrorManager(errors);

  return <>
    <FieldRow>
      <FieldLabel variant="body1">Describe the test prep your organization offers</FieldLabel>
      <TextArea disabled={readOnly} validation={hasError('description') ? 'error' : undefined} validationMsg={getError('description')}
        onChange={(e) => { changed({ description: e.target.value }); }}
        value={description}
      />
    </FieldRow>
    <FieldRow>
      <RadioLabel variant="body1">Does your organization allow outside students to participate in test prep?</RadioLabel>
      <BooleanRadio readOnly={readOnly} error={getError('allowOutsideStudents')} value={allowOutsideStudents} name='allowOutsideStudents'
        onChange={(value) => { changed({ allowOutsideStudents: value }); }} />
    </FieldRow>
    <FieldRow>
      <RadioLabel variant="body1">Does your organization provide additional compensation for test prep staff?</RadioLabel>
      <BooleanRadio readOnly={readOnly} error={getError('hasAdditionalCompensation')} value={hasAdditionalCompensation} name='hasAdditionalCompensation'
        onChange={(value) => { changed({ hasAdditionalCompensation: value }); }} />
    </FieldRow>
  </>;
};

const Staff: FC<IFieldProps<IStaff>> = ({ item: initial, errors, readOnly, onUpdate }) => {
  const {
    count,
    hasBackgroundChecks,
    noBackgroundCheckReason
  } = initial;

  const changed = (updates: Draft<IStaff>) => {
    if (onUpdate) {
      onUpdate({ ...initial, ...updates });
    }
  };

  const { getError, hasError } = ErrorManager(errors);

  return <>
    <FieldRow>
      <FieldLabel variant="body1">How many staff members does your organization have?</FieldLabel>
      <IntegerInput name='staff.count' readOnly={readOnly} value={count} onChange={(value) => changed({ count: value })} error={getError('count')} />
    </FieldRow>
    <FieldRow>
      <RadioLabel variant="body1">Does your organization perform background checks on all staff members?</RadioLabel>
      <BooleanRadio readOnly={readOnly} error={getError('hasBackgroundChecks')} value={hasBackgroundChecks} name='hasBackgroundChecks'
        onChange={(value) => {
          changed({
            hasBackgroundChecks: value,
            noBackgroundCheckReason: value ? noBackgroundCheckReason : undefined
          });
        }} />
    </FieldRow>
    {(hasBackgroundChecks === false) &&
      <FieldRow>
        <FieldLabel variant="body1">Why does your organization not perform background checks on all staff?</FieldLabel>
        <TextArea
          disabled={readOnly}
          validation={hasError('noBackgroundCheckReason') ? 'error' : undefined}
          validationMsg={getError('noBackgroundCheckReason')}
          onChange={(e) => { changed({ noBackgroundCheckReason: e.target.value }); }}
          value={noBackgroundCheckReason || ''}
        />
      </FieldRow>
    }
  </>;
};

export interface IAIAFieldProps {
  categoryId: string;
  userId: string;
  appliedLevel: '1' | '2' | undefined;
  readOnly?: boolean;
  item: Draft<IAIA>;
  validating?: boolean;
  errors: IFieldErrors;
  original?: Draft<IAIA>;
  showDiff?: boolean;
  onUpdate?: (address: Draft<IAIA>) => void;
}

export const AIAFields: FC<IAIAFieldProps> = ({ appliedLevel, categoryId, item: initial, readOnly, errors, onUpdate }) => {
  const { userName } = useAuth();

  /**
     * Form Variables and Monitoring Funcationality
     *
     * This is the place where most additions and modifications will occur for the form
     * in addition to the rendering block.
     */
  const {
    name,
    website,
    phone,
    fax,
    email,
    existingOrg,
    existingOrgInfo,
    sharedOrgAddress,
    sharedOrgAddressInfo,
    isCBReferral,
    referral,
    establishedDate,
    typeOther,
    studentCount,
    reasonForApplication,
    hasTestPrep
  } = initial;

  const type: string[] = initial.type || [];
  const supportFiles: IMaterial[] = initial.supportFiles || [];
  const visitingAddress: Draft<IAddress> = initial.visitingAddress || { countryIsoCd: 'US' };
  const shippingAddress: Draft<IAddress> | undefined = initial.shippingAddress;
  const staff: Draft<IStaff> = initial.staff || {};
  const examSecurity: Draft<IExamSecurity> = initial.examSecurity || {};
  const testPrep: Draft<ITestPrep> = initial.testPrep || {};

  const { getError, getFieldError, hasError } = ErrorManager(errors);

  const supportRemove = (fileName: string) => {
    const updated = [...supportFiles];
    for (let x = 0; x < updated.length; x++) {
      if (updated[x].fileName === fileName) {
        updated.splice(x, 1);
      }
    }
    return updated;
  };

  const supportAdd = (item: IMaterial) => {
    const updated = supportRemove(item.fileName);
    updated.push(item);
    return updated;
  };

  const staffErrors = getFieldError('staff');

  const changed = (updates: Draft<IAIA>) => {
    if (onUpdate) {
      onUpdate({ ...initial, ...updates });
    }
  };

  return <>
    <Panel>
      <Title>Institution Information</Title>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow>
            <FieldLabel variant="body1">Institution Name</FieldLabel>
            <Input disabled={readOnly} value={name} validation={hasError('name') ? 'error' : undefined} validationMsg={getError('name')}
              onChange={(e) => { changed({ name: e.target.value }); }}
            />
          </FieldRow>
          <FieldRow>
            <FieldLabel variant="body1">Number of students attending institution?</FieldLabel>
            <IntegerInput name='studentCount' readOnly={readOnly} value={studentCount} error={getError('studentCount')}
              onChange={(value) => { changed({ studentCount: value }); }}
            />
          </FieldRow>
          <FieldRow>
            {readOnly ?
              <FieldLabel>Established Date: {convertToFormattedDate(establishedDate)}</FieldLabel> :
              <>
                <FieldLabel variant="body1">Date your organization was established?</FieldLabel>
                <Input type="date" value={convertToIsoDate(establishedDate)}
                  validation={getError('establishedDate') ? 'error' : undefined}
                  validationMsg={getError('establishedDate')}
                  onChange={(e) => { changed({ establishedDate: e.target.value }); }} />
              </>
            }

          </FieldRow>
          <FieldRow>
            <>
              <FieldLabel variant="body1" style={{ marginBottom: '4px' }}>Institution Type</FieldLabel>
              <Checkboxes error={getError('type')} value={type} readOnly={readOnly}
                options={TYPE_OPTIONS.map((val: string) => ({ key: val, label: val }))}
                onChange={(values: string[]) => {
                  changed({
                    type: values,
                    typeOther: !values.includes(KEY_OTHER) ? undefined : typeOther
                  });
                }} />
            </>
          </FieldRow>
          {type.includes(KEY_OTHER) &&
            <FieldRow>
              <FieldLabel variant="body1">Specify the institution type that isn't represented in the options:</FieldLabel>
              <Input disabled={readOnly} value={typeOther} validation={hasError('typeOther') ? 'error' : undefined} validationMsg={getError('typeOther')}
                onChange={(e) => { changed({ typeOther: e.target.value }); }}
              />
            </FieldRow>
          }

          <FieldRow>
            <FieldLabel variant="body1">Please explain your institution's need for a College Board code.</FieldLabel>
            <TextArea disabled={readOnly} validation={hasError('reasonForApplication') ? 'error' : undefined} validationMsg={getError('reasonForApplication')}
              onChange={(e: any) => { changed({ reasonForApplication: e.target.value }); }} value={reasonForApplication} />
          </FieldRow>
        </Grid>
        <Grid item xs={6}>
          <FieldRow>
            <FieldLabel variant="body1">Please upload your organization's organizing documents, mission documentation, 501C4 (if applicable) and any other relevant documentation.</FieldLabel>
            {hasError('supportFiles') &&
              <span style={{ marginBottom: '8px' }} className='cb-input-helper cb-validation-error'>{getError('supportFiles')}</span>
            }
          </FieldRow>

          <FieldRow>
            {supportFiles.map((item, i) => {
              return <FieldRow key={`support-file-${i}`}><FileDetails data={item} readOnly={readOnly} onRemove={(item) => {
                changed({ supportFiles: supportRemove(item.fileName) });
              }} /></FieldRow>;
            })}
          </FieldRow>
          {!readOnly &&
            <>
              <div style={{ marginBottom: '10px', borderBottom: '1px solid #EEE' }}></div>
              <FieldRow>
                <FileUploader
                  uploadButton='Add file'
                  onUploadFile={async (file: File, description: string | undefined) => {
                    const material: IMaterial = await uploadFile(userName, { categoryId: categoryId }, file, description, undefined, true);
                    changed({ supportFiles: supportAdd(material) });
                  }}
                />
              </FieldRow>
            </>
          }
        </Grid>
      </Grid>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow>
            <FieldLabel variant="body1">Phone</FieldLabel>
            <Input disabled={readOnly} validation={hasError('phone') ? 'error' : undefined} validationMsg={getError('phone')}
              onChange={(e) => { changed({ phone: e.target.value || undefined }); }}
              value={phone || ''}
            />
          </FieldRow>
          <FieldRow>
            <FieldLabel variant="body1">Fax</FieldLabel>
            <Input disabled={readOnly} validation={hasError('fax') ? 'error' : undefined} validationMsg={getError('fax')}
                   onChange={(e) => { changed({ fax: e.target.value || undefined }); }}
                   value={fax || ''}
            />
          </FieldRow>
          <FieldRow>
            <FieldLabel variant="body1">Website</FieldLabel>
            <Input disabled={readOnly} validation={hasError('website') ? 'error' : undefined} validationMsg={getError('website')}
              onChange={(e) => { changed({ website: e.target.value || undefined }); }}
              value={website || ''}
            />
          </FieldRow>
          <FieldRow>
            <FieldLabel variant="body1">Email</FieldLabel>
            <Input disabled={readOnly} validation={hasError('email') ? 'error' : undefined} validationMsg={getError('email')}
              onChange={(e) => { changed({ email: e.target.value || undefined }); }}
              value={email || ''}
            />
          </FieldRow>
        </Grid>
        <Grid item xs={6}>
          <FieldRow>
            <RadioLabel variant="body1">Do your organization share an address with another organization?</RadioLabel>
            <BooleanRadio readOnly={readOnly} error={getError('sharedOrgAddress')} value={sharedOrgAddress} name='sharedOrgAddress'
              onChange={(value) => {
                changed({
                  sharedOrgAddress: value,
                  sharedOrgAddressInfo: value ? sharedOrgAddressInfo : undefined
                });
              }} />
          </FieldRow>
          {sharedOrgAddress &&
            <FieldRow>
              <FieldLabel variant="body1">List the organizations using this address:</FieldLabel>
              <Input
                disabled={readOnly}
                validation={hasError('sharedOrgAddressInfo') ? 'error' : undefined}
                validationMsg={getError('sharedOrgAddressInfo')}
                onChange={(e) => { changed({ sharedOrgAddressInfo: e.target.value }); }}
                value={sharedOrgAddressInfo}
              />
            </FieldRow>
          }
          <FieldRow>
            <RadioLabel variant="body1">Was your organization referred by someone from the College Board?</RadioLabel>
            <BooleanRadio readOnly={readOnly} error={getError('isCBReferral')} value={isCBReferral} name='isCBReferral'
              onChange={(value) => {
                changed({
                  isCBReferral: value,
                  referral: value ? referral : undefined
                });
              }} />
          </FieldRow>
          {isCBReferral &&
            <Referral item={referral || {}} onUpdate={(data) => changed({ referral: data })} errors={getFieldError('referral')} readOnly={readOnly} />
          }

          <FieldRow>
            <RadioLabel variant="body1">Has your organization ever used another name, address or College Board school code?</RadioLabel>
            <BooleanRadio readOnly={readOnly} error={getError('existingOrg')} value={existingOrg} name='existingOrg' onChange={(value) => {
              changed({
                existingOrg: value,
                existingOrgInfo: value ? existingOrgInfo : undefined
              });
            }} />
          </FieldRow>
          {existingOrg &&
            <FieldRow>
              <FieldLabel variant="body1">Enter old school information here</FieldLabel>
              <Input disabled={readOnly} validation={hasError('existingOrgInfo') ? 'error' : undefined} validationMsg={getError('existingOrgInfo')}
                onChange={(e) => {
                  changed({ existingOrgInfo: e.target.value });
                }}
                value={existingOrgInfo}
              />
            </FieldRow>
          }
        </Grid>
      </Grid>
    </Panel>
    <Panel>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow><strong>Visiting Address</strong></FieldRow>
          <AddressFields readOnly={readOnly} errors={getFieldError('visitingAddress')} address={initial.visitingAddress}
            onUpdate={(address) => {
              changed({ visitingAddress: address });
            }} />
        </Grid>
        <Grid item xs={6}>
          <FieldRow><Checkbox disabled={examSecurity?.heldAtShippingAddress || readOnly} label={<strong>Shipping Address</strong>} style={{ display: 'inline-block' }} checked={shippingAddress ? true : false}
            onChange={() => {
              if (!shippingAddress) {
                changed({
                  shippingAddress: {
                    countryIsoCd: visitingAddress.countryIsoCd || 'US',
                    stateCd: visitingAddress.stateCd,
                    province: visitingAddress.province
                  }
                });
              } else {
                changed({ shippingAddress: undefined });
              }
            }} />
            {hasError('shippingAddress') &&
              <span className='cb-input-helper cb-validation-error'>{getError('shippingAddress')}</span>
            }
          </FieldRow>
          {shippingAddress &&
            <AddressFields readOnly={readOnly} errors={getFieldError('shippingAddress')} address={initial.shippingAddress}
              onUpdate={(address) => {
                changed({ shippingAddress: address });
              }} />
          }
        </Grid>
      </Grid>
    </Panel>

    {appliedLevel === '2' &&
      <>
        <Panel>
          <Title>Test Prep & Staff</Title>
          <Grid container spacing={5}>
            <Grid item xs={6}>
              <FieldRow>
                <RadioLabel variant="body1">Does your organization offer test prep?</RadioLabel>
                <BooleanRadio readOnly={readOnly} error={getError('hasTestPrep')} value={hasTestPrep} name='hasTestPrep'
                  onChange={(value) => {
                    changed({
                      hasTestPrep: value,
                      testPrep: value ? { ...testPrep } : undefined
                    });
                  }} />
              </FieldRow>
              {hasTestPrep &&
                <TestPrep item={testPrep} onUpdate={(data) => {
                  changed({ testPrep: data });
                }} errors={getFieldError('testPrep')} readOnly={readOnly} />
              }
            </Grid>
            <Grid item xs={6}>
              <Staff item={staff} onUpdate={(data) => {
                changed({ staff: data });
              }} errors={staffErrors} readOnly={readOnly} />
            </Grid>
          </Grid>
        </Panel>
        <Panel>
          <Title>Exam Security</Title>
          <ExamSecurityFields readOnly={readOnly} onUpdate={(data) => {
            const update: Draft<IAIA> = {
              examSecurity: data
            };
            if (data.heldAtShippingAddress && !shippingAddress) {
              update.shippingAddress = {
                countryIsoCd: visitingAddress.countryIsoCd || 'US',
                stateCd: visitingAddress.stateCd,
                province: visitingAddress.province
              };
            }
            changed(update);
          }}
            errors={getFieldError('examSecurity')}
            item={examSecurity}
          />
        </Panel>
      </>
    }
  </>;
};
